import { createSignal, createContext, useContext, For, Show } from "solid-js";

const ToastContext = createContext<ToastProviderProps>();

export interface ToastAction {
    actionLabel: string;
    onAction: () => void;
}

export interface ToastProviderProps {
    info: (msg: string, action?: ToastAction) => void;
    warning: (msg: string, action?: ToastAction) => void;
    error: (msg: string, action?: ToastAction) => void;
    success: (msg: string, action?: ToastAction) => void;
}

interface ToastEntry {
    id: number;
    message: string;
    level: "info" | "warning" | "error" | "success";
    leaving: boolean;
    action?: ToastAction;
}

const TOAST_LIFETIME_MS = 5000;
const TOAST_LEAVE_MS = 200;

export function ToastProvider(props: any) {
    const [toasts, setToasts] = createSignal<ToastEntry[]>([]);

    const removeToast = (id: number) => {
        setToasts(toasts().filter((toast) => toast.id !== id));
    };

    const beginLeaving = (id: number) => {
        setToasts(toasts().map((t) => (t.id === id ? { ...t, leaving: true } : t)));
        setTimeout(() => removeToast(id), TOAST_LEAVE_MS);
    };

    const addToast = (message: string, level: ToastEntry["level"], action?: ToastAction) => {
        const id = Date.now() + Math.floor(Math.random() * 1000);
        setToasts([...toasts(), { id, message, level, leaving: false, action }]);
        setTimeout(() => beginLeaving(id), TOAST_LIFETIME_MS);
    };

    const toast: ToastProviderProps = {
        info: (msg: string, action?: ToastAction) => addToast(msg, "info", action),
        warning: (msg: string, action?: ToastAction) => addToast(msg, "warning", action),
        error: (msg: string, action?: ToastAction) => addToast(msg, "error", action),
        success: (msg: string, action?: ToastAction) => addToast(msg, "success", action)
    };

    return (
        <ToastContext.Provider value={toast}>
            {props.children}
            <div class="toast-container">
                <For each={toasts()}>
                    {(t) => (
                        <Toast
                            message={t.message}
                            level={t.level}
                            leaving={t.leaving}
                            action={t.action}
                            onDismiss={() => beginLeaving(t.id)}
                        />
                    )}
                </For>
            </div>
        </ToastContext.Provider>
    );
}

function Toast(props: { message: string; level: string; leaving: boolean; action?: ToastAction; onDismiss: () => void }) {
    return (
        <div class={`toast ${props.level}${props.leaving ? " toast-leaving" : ""}`} onClick={props.onDismiss}>
            <p>{props.message}</p>
            <Show when={props.action}>
                {(action) => (
                    <button type="button" class="toast-action" onClick={(e) => { e.stopPropagation(); action().onAction(); props.onDismiss(); }}>
                        {action().actionLabel}
                    </button>
                )}
            </Show>
        </div>
    );
}

export function useToast() : ToastProviderProps | undefined {
    return useContext(ToastContext);
}
